import type { ReactElement } from 'react';
import React, { useState } from 'react';
import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import type { ColumnDef, SortingState, Table } from '@tanstack/react-table';
import { type NoResultsAlertProps } from 'atom-components/tables/shared/no-results-alert';
import TanStackTable from 'atom-components/tables/shared/tanstack-table';
import type { RowSelectionProps, SortingProps, WithOptionalId } from 'atom-components/tables/types';

type Props<TData extends WithOptionalId> = {
  isLoading?: boolean; // Client controlled tables may not need a loading state, as all of the data would be fetched before rendering.
  data: (TData & WithOptionalId)[];
  columns: ColumnDef<TData>[];
} & SortingProps &
  RowSelectionProps &
  NoResultsAlertProps;

/**
 * Accepts TanStack Table 8+ data and columns are props and sets up a table that can be sorted
 * without an external sorting mechanism.
 */
export default function ClientControlledTable<TData extends object>({
  isLoading,
  data,
  columns,
  initialSorting,
  onSort,
  rowSelection,
  setRowSelection,
  entity,
  hideSuggestion
}: Props<TData & WithOptionalId>): ReactElement {
  const [sorting, setSorting] = useState<SortingState>(initialSorting || []);

  function handleSortingChange(updatedSorting: SortingState): void {
    setSorting(updatedSorting);
    // An external sorting function is not required for client-side sorting, but optionally you
    // can inform some external state with the updated sorting, for example if we wanted to update
    // the URL with the updated sorting state:
    if (onSort) {
      onSort(updatedSorting);
    }
  }

  const tableInstance: Table<TData> = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(), // Provide a sorting row model for client-side sorting
    state: { sorting, rowSelection },

    // Column Sorting:
    manualSorting: false, // Enable client-side sorting
    onSortingChange: handleSortingChange,

    // Row Selection:
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    // An "id" field must be added to the row for identifying selections. We are requiring the field
    // to be an integer named "id" for consistency. TanStack Table expects this ID to be a string
    // when it is used as a key in the RowSelectionState type, so you will need to convert it back
    // to an integer when the selected ids from the RowSelectionState are used.
    getRowId: (row, index) => (row?.id ? row.id.toString() : row[index])

    // Todo: allow client side tables to be paginated.
  });

  return (
    <TanStackTable
      isLoading={isLoading}
      tableInstance={tableInstance}
      entity={entity}
      hideSuggestion={hideSuggestion}
    />
  );
}
